import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../Navbar";
import { smartLinksAdmin, LINK_TYPES, checkDestinationUrl } from "../../services/smartLinkApi";
import "../AdminDashboard.css";
import "./AdminSmartLinks.css";

// =====================================================
// ADMIN — MANAGE LINKS (its own page, /smart-links-admin)
//   1. Analytics — total / unique Smart Link clicks, active / disabled links
//   2. Settings  — Card Interval (every Nth blog card) + Redirect Cooldown
//   3. Add / edit a link
//   4. All links — per-link clicks, enable/disable, edit, delete
// Every number shown comes from the server; nothing is counted here.
// =====================================================

const EMPTY_FORM = { name: "", url: "", type: "monetag", active: true };

const errText = (err, fallback) => err?.response?.data?.message || fallback;
const fmtDate = (d) => (d ? new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");
const fmtCount = (n) => Number(n || 0).toLocaleString("en-IN");
const typeLabel = (t) => LINK_TYPES.find((x) => x.value === t)?.label || t;

function AdminSmartLinks() {
  const [links, setLinks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [settings, setSettings] = useState({ cardInterval: "", cooldownMinutes: "" });
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState(null); // { ok, text }

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [formBusy, setFormBusy] = useState(false);
  const [formMsg, setFormMsg] = useState(null);

  const [busyId, setBusyId] = useState(null);
  const [rowMsg, setRowMsg] = useState(null);

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const [analytics, s] = await Promise.all([smartLinksAdmin.getAnalytics(), smartLinksAdmin.getSettings()]);
      setLinks(analytics.links);
      setSummary(analytics.summary);
      setSettings({ cardInterval: String(s.cardInterval), cooldownMinutes: String(s.cooldownMinutes) });
    } catch (err) {
      setLoadError(errText(err, "Could not load Smart Links"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ── settings ──
  const saveSettings = async (e) => {
    e.preventDefault();
    const cardInterval = Number(settings.cardInterval);
    const cooldownMinutes = Number(settings.cooldownMinutes);
    if (!Number.isInteger(cardInterval) || cardInterval < 1) {
      setSettingsMsg({ ok: false, text: "Card Interval must be a whole number of at least 1" });
      return;
    }
    if (!Number.isInteger(cooldownMinutes) || cooldownMinutes < 1) {
      setSettingsMsg({ ok: false, text: "Redirect Cooldown must be a whole number of minutes, at least 1" });
      return;
    }
    setSettingsBusy(true);
    setSettingsMsg(null);
    try {
      const s = await smartLinksAdmin.saveSettings({ cardInterval, cooldownMinutes });
      setSettings({ cardInterval: String(s.cardInterval), cooldownMinutes: String(s.cooldownMinutes) });
      setSettingsMsg({ ok: true, text: "Settings saved" });
    } catch (err) {
      setSettingsMsg({ ok: false, text: errText(err, "Could not save settings") });
    } finally {
      setSettingsBusy(false);
    }
  };

  // ── add / edit ──
  const startEdit = (link) => {
    setEditingId(link.id);
    setForm({ name: link.name, url: link.url, type: link.type, active: link.active });
    setFormMsg(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormMsg(null);
  };

  const submitLink = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setFormMsg({ ok: false, text: "Link name is required" });
      return;
    }
    const urlError = checkDestinationUrl(form.url);
    if (urlError) {
      setFormMsg({ ok: false, text: urlError });
      return;
    }
    setFormBusy(true);
    setFormMsg(null);
    const body = { name: form.name.trim(), url: form.url.trim(), type: form.type, active: form.active };
    try {
      if (editingId) await smartLinksAdmin.updateLink(editingId, body);
      else await smartLinksAdmin.createLink(body);
      setFormMsg({ ok: true, text: editingId ? "Link updated" : "Link added" });
      setEditingId(null);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      setFormMsg({ ok: false, text: errText(err, "Could not save the link") });
    } finally {
      setFormBusy(false);
    }
  };

  // ── row actions ──
  const toggleActive = async (link) => {
    setBusyId(link.id);
    setRowMsg(null);
    try {
      await smartLinksAdmin.setStatus(link.id, !link.active);
      await load();
    } catch (err) {
      setRowMsg(errText(err, "Could not change the link's status"));
    } finally {
      setBusyId(null);
    }
  };

  const removeLink = async (link) => {
    if (!window.confirm(`Delete "${link.name}"? It stops being shown on blog cards immediately.`)) return;
    setBusyId(link.id);
    setRowMsg(null);
    try {
      await smartLinksAdmin.deleteLink(link.id);
      if (editingId === link.id) cancelEdit();
      await load();
    } catch (err) {
      setRowMsg(errText(err, "Could not delete the link"));
    } finally {
      setBusyId(null);
    }
  };

  const interval = Number(settings.cardInterval) || 0;
  const examplePositions = interval > 0 ? [1, 2, 3, 4].map((k) => k * interval).join(", ") : "—";

  return (
    <div className="admin-root">
      <Navbar />
      <div className="admin-page">
        <div className="admin-hero">
          <h1 className="admin-title" style={{ fontSize: "clamp(24px, 7.5vw, 42px)", overflowWrap: "anywhere" }}>
            🔗 Manage Links
          </h1>
          <p className="admin-sub">
            Smart Links on every Nth blog card in Read Blogs, Profile and Author Profile — rotation, redirect cooldown and click analytics
          </p>
          <Link to="/admin" className="sl-back-link">← Back to Admin Dashboard</Link>
        </div>

        {loading && <p className="admin-empty">Loading…</p>}
        {loadError && (
          <p className="sl-msg sl-msg-error" role="alert">
            {loadError} <button type="button" className="admin-btn" onClick={load}>Retry</button>
          </p>
        )}

        {/* ══ ANALYTICS ══ */}
        {summary && (
          <div className="admin-stats-row" data-testid="sl-summary">
            {[
              { icon: "🖱️", num: summary.totalClicks, label: "Total Smart Link Clicks" },
              { icon: "👤", num: summary.uniqueClicks, label: "Total Unique Clicks" },
              { icon: "✅", num: summary.activeLinks, label: "Active Links" },
              { icon: "⏸️", num: summary.disabledLinks, label: "Disabled Links" },
            ].map((s) => (
              <div className="admin-stat-card" key={s.label}>
                <span className="admin-stat-icon">{s.icon}</span>
                <span className="admin-stat-num">{fmtCount(s.num)}</span>
                <span className="admin-stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* ══ SETTINGS ══ */}
        <div className="admin-section">
          <div className="admin-section-header">
            <h2 className="admin-section-title">⚙️ Settings</h2>
          </div>
          <form className="sl-form" onSubmit={saveSettings}>
            <label className="sl-field">
              <span className="sl-label">Card Interval</span>
              <input
                className="admin-search sl-input"
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                value={settings.cardInterval}
                onChange={(e) => setSettings((s) => ({ ...s, cardInterval: e.target.value }))}
                aria-label="Card Interval"
              />
              <span className="sl-hint">Cards {examplePositions} … get a link</span>
            </label>
            <label className="sl-field">
              <span className="sl-label">Redirect Cooldown (Minutes)</span>
              <input
                className="admin-search sl-input"
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                value={settings.cooldownMinutes}
                onChange={(e) => setSettings((s) => ({ ...s, cooldownMinutes: e.target.value }))}
                aria-label="Redirect Cooldown (Minutes)"
              />
              <span className="sl-hint">One external redirect per user per this many minutes. Applies to new cooldowns.</span>
            </label>
            <div className="sl-actions">
              <button type="submit" className="admin-btn btn-success" disabled={settingsBusy}>
                {settingsBusy ? "Saving…" : "Save Settings"}
              </button>
              {settingsMsg && (
                <span className={`sl-msg ${settingsMsg.ok ? "sl-msg-ok" : "sl-msg-error"}`} role="status">
                  {settingsMsg.text}
                </span>
              )}
            </div>
          </form>
        </div>

        {/* ══ ADD / EDIT ══ */}
        <div className="admin-section">
          <div className="admin-section-header">
            <h2 className="admin-section-title">{editingId ? "✏️ Edit Link" : "➕ Add Link"}</h2>
          </div>
          <form className="sl-form" onSubmit={submitLink}>
            <label className="sl-field">
              <span className="sl-label">Link Name</span>
              <input
                className="admin-search sl-input"
                maxLength={80}
                placeholder="e.g. Monetag Link 1"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                aria-label="Link Name"
              />
            </label>
            <label className="sl-field sl-field-wide">
              <span className="sl-label">Destination URL</span>
              <input
                className="admin-search sl-input"
                type="url"
                maxLength={2048}
                placeholder="https://"
                value={form.url}
                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                aria-label="Destination URL"
              />
            </label>
            <label className="sl-field">
              <span className="sl-label">Link Type</span>
              <select
                className="admin-search sl-input"
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                aria-label="Link Type"
              >
                {LINK_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </label>
            <label className="sl-check">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              />
              Active
            </label>
            <div className="sl-actions">
              <button type="submit" className="admin-btn btn-success" disabled={formBusy}>
                {formBusy ? "Saving…" : editingId ? "Save Changes" : "Add Link"}
              </button>
              {editingId && (
                <button type="button" className="admin-btn" onClick={cancelEdit} disabled={formBusy}>Cancel</button>
              )}
              {formMsg && (
                <span className={`sl-msg ${formMsg.ok ? "sl-msg-ok" : "sl-msg-error"}`} role="status">
                  {formMsg.text}
                </span>
              )}
            </div>
          </form>
        </div>

        {/* ══ LINKS + PER-LINK ANALYTICS ══ */}
        <div className="admin-section">
          <div className="admin-section-header">
            <h2 className="admin-section-title">🔗 All Links ({links.length})</h2>
          </div>
          {rowMsg && <p className="sl-msg sl-msg-error" role="alert">{rowMsg}</p>}
          {!loading && links.length === 0 ? (
            <p className="admin-empty">No links yet. Blog cards open normally until you add an active link.</p>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Rotation</th>
                    <th>Link Name</th>
                    <th>Destination URL</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Total Clicks</th>
                    <th>Unique Clicks</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let slot = 0;
                    return links.map((l) => {
                      if (l.active) slot += 1;
                      return (
                        <tr key={l.id} data-testid={`sl-row-${l.id}`}>
                          <td className="admin-td-num">{l.active ? `#${slot}` : "—"}</td>
                          <td>{l.name}</td>
                          <td className="sl-url">
                            <a href={l.url} target="_blank" rel="noopener noreferrer">{l.url}</a>
                          </td>
                          <td>{typeLabel(l.type)}</td>
                          <td>
                            <span className={`admin-badge ${l.active ? "badge-active" : "badge-blocked"}`}>
                              {l.active ? "Active" : "Disabled"}
                            </span>
                          </td>
                          <td className="admin-td-num">{fmtCount(l.totalClicks)}</td>
                          <td className="admin-td-num">{fmtCount(l.uniqueClicks)}</td>
                          <td className="admin-td-muted">{fmtDate(l.createdAt)}</td>
                          <td>
                            <div className="admin-action-group">
                              <button type="button" className="admin-btn" disabled={busyId === l.id} onClick={() => startEdit(l)}>
                                Edit
                              </button>
                              <button
                                type="button"
                                className={`admin-btn ${l.active ? "btn-warn" : "btn-success"}`}
                                disabled={busyId === l.id}
                                onClick={() => toggleActive(l)}
                              >
                                {l.active ? "Disable" : "Enable"}
                              </button>
                              <button type="button" className="admin-btn btn-danger" disabled={busyId === l.id} onClick={() => removeLink(l)}>
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminSmartLinks;
